import { arrangementScenes, boardLayouts, boardStyles, lifestyleScenes, packshotOrientations, stagingScenes, defaultArrangementSceneKeys, type ModeKey } from "../../lib/generationModes";
import { pick } from "../../lib/i18n";
import { linesFromFeatures, toggleCsvValue } from "../../lib/productPresentation";
import type { Product } from "../../types";

type Props = {
  modeKey: ModeKey;
  config: Record<string, string>;
  onChange: (key: string, value: string) => void;
  product: Product;
};

const copy = {
  pl: {
    boardLayout: "Układ planszy",
    boardStyle: "Styl planszy",
    scenery: "Sceneria",
    preset: "Preset",
    custom: "Własna",
    customPlural: "Własne",
    stagingCustomPlaceholder: "Opisz scenę np. 'przytulna biblioteka z ciemnymi drewnianymi półkami…'",
    multiShot: "Multi-shot: 5 perspektyw tej samej sceny na jednej planszy",
    skipBoardShots: "Pomiń planszę - zwróć ujęcia osobno",
    variation: "Wymuś różnorodność ujęć (inne światło, kadr i rekwizyt w każdym ujęciu)",
    cameraOrientation: "Orientacja kamery",
    accessories: "Akcesoria w kadrze (opcjonalnie)",
    accessoriesPlaceholder: "np. laptop, notatnik, kubek kawy",
    lifestyleScene: "Scena lifestyle",
    lifestyleCustomPlaceholder: "Opisz scenę i kontekst użycia z osobą, np. 'osoba otwierająca nowoczesne drzwi zewnętrzne wejściowe w przedpołudniowym świetle, wejście do domu jednorodzinnego'",
    variantsList: "Lista wariantów (jedna linia: NAZWA | opis koloru)",
    eachVariantImage: "Każdy wariant to osobna grafika.",
    variantsPlaceholder: "MESH BLACK | czarna siatka, czarne wykończenia\nMESH GREY | szara siatka, szare wykończenia",
    packshotOrientation: "Orientacja packshotu",
    boardTitle: "Tytuł planszy (opcjonalnie)",
    skipBoardPhotos: "Pomiń planszę - zwróć zdjęcia osobno",
    arrangementScenery: "Scenerie aranżacji",
    eachSceneImage: "Każda scena to osobna grafika.",
    arrangementsCustomPlaceholder: "Jedna sceneria na linię, maksymalnie 4, np. jasny showroom z naturalnym drewnem",
    dimensionsToShow: "Wymiary do pokazania",
    noDimensions: "Brak wymiarów w karcie produktu. Gemini spróbuje oznaczyć dostępne wymiary automatycznie.",
    calloutsLabel: "Callouty detali",
    featureBoardLabel: "Cechy na planszy",
    lineNameFocus: "(jedna linia: NAZWA | opis/focus)",
    featuresPlaceholder: "Regulowany zagłówek | zbliżenie na mechanizm regulacji\nOddychająca siatka | faktura materiału oparcia",
    skipBoardHeroDetails: "Pomiń planszę - zwróć hero i detale osobno",
    certsLabel: "Certyfikaty / argumenty zaufania",
    certsPlaceholder: "EN 1335\nBIFMA\n5 lat gwarancji",
    faqLabel: "FAQ (jedna linia: PYTANIE | ODPOWIEDŹ)",
    faqPlaceholder: "Czy fotel pasuje do pracy 8h? | Tak, ma ergonomiczne podparcie i regulacje.\nJakie są warianty tapicerki? | Dostępne warianty zależą od konfiguracji produktu.",
    customLabel: "Opisz co chcesz wygenerować",
    customPlaceholder: "np. Fotel na tle panoramy miasta, wieczorne oświetlenie, elegancka atmosfera biurowa",
    customHint: "AI przygotuje profesjonalny prompt fotograficzny na podstawie Twojego opisu i danych produktu.",
    problemSolutionLabel: "Problem / rozwiązanie (jedna linia: PROBLEM | ROZWIĄZANIE)",
    problemSolutionPlaceholder: "Zmęczenie przy długiej pracy | Ergonomiczne podparcie pleców i regulacje.\nNiespójne stanowisko pracy | Produkt porządkuje i uspokaja przestrzeń.",
  },
  en: {
    boardLayout: "Board layout",
    boardStyle: "Board style",
    scenery: "Scenery",
    preset: "Preset",
    custom: "Custom",
    customPlural: "Custom",
    stagingCustomPlaceholder: "Describe the scene, e.g. 'a cozy library with dark wooden shelves…'",
    multiShot: "Multi-shot: 5 perspectives of the same scene on one board",
    skipBoardShots: "Skip the board - return shots separately",
    variation: "Force shot variation (different light, framing and prop per shot)",
    cameraOrientation: "Camera orientation",
    accessories: "Accessories in frame (optional)",
    accessoriesPlaceholder: "e.g. laptop, notebook, coffee mug",
    lifestyleScene: "Lifestyle scene",
    lifestyleCustomPlaceholder: "Describe the scene and usage context with a person, e.g. 'a person opening a modern front door in late-morning light, entrance to a single-family house'",
    variantsList: "Variant list (one per line: NAME | color description)",
    eachVariantImage: "Each variant is a separate image.",
    variantsPlaceholder: "MESH BLACK | black mesh, black finishes\nMESH GREY | gray mesh, gray finishes",
    packshotOrientation: "Packshot orientation",
    boardTitle: "Board title (optional)",
    skipBoardPhotos: "Skip the board - return photos separately",
    arrangementScenery: "Arrangement scenes",
    eachSceneImage: "Each scene is a separate image.",
    arrangementsCustomPlaceholder: "One scene per line, up to 4, e.g. a bright showroom with natural wood",
    dimensionsToShow: "Dimensions to show",
    noDimensions: "No dimensions in the product card. Gemini will try to label available dimensions automatically.",
    calloutsLabel: "Detail callouts",
    featureBoardLabel: "Features on the board",
    lineNameFocus: "(one per line: NAME | description/focus)",
    featuresPlaceholder: "Adjustable headrest | close-up of the adjustment mechanism\nBreathable mesh | texture of the backrest material",
    skipBoardHeroDetails: "Skip the board - return hero and details separately",
    certsLabel: "Certificates / trust arguments",
    certsPlaceholder: "EN 1335\nBIFMA\n5-year warranty",
    faqLabel: "FAQ (one per line: QUESTION | ANSWER)",
    faqPlaceholder: "Is the chair suitable for 8-hour work? | Yes, it has ergonomic support and adjustments.\nWhat upholstery variants are available? | Available variants depend on the product configuration.",
    customLabel: "Describe what you want to generate",
    customPlaceholder: "e.g. A chair against a city skyline, evening lighting, an elegant office atmosphere",
    customHint: "The AI will craft a professional photographic prompt from your description and the product data.",
    problemSolutionLabel: "Problem / solution (one per line: PROBLEM | SOLUTION)",
    problemSolutionPlaceholder: "Fatigue during long work | Ergonomic back support and adjustments.\nInconsistent workspace | The product tidies and calms the space.",
  },
};

function BoardOptions({ config, onChange }: { config: Record<string, string>; onChange: (key: string, value: string) => void }) {
  const t = pick(copy);
  if (config.skip_board === "true") return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      <div>
        <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.boardLayout}</div>
        <select
          className="v3-select"
          value={config.board_layout || "auto"}
          onChange={(event) => onChange("board_layout", event.target.value)}
        >
          {boardLayouts().map((option) => (
            <option key={option.k} value={option.k}>{option.n}</option>
          ))}
        </select>
      </div>
      <div>
        <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.boardStyle}</div>
        <select
          className="v3-select"
          value={config.board_style || "auto"}
          onChange={(event) => onChange("board_style", event.target.value)}
        >
          {boardStyles().map((option) => (
            <option key={option.k} value={option.k}>{option.n}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default function ModeConfigPanel({ modeKey, config, onChange, product }: Props) {
  const t = pick(copy);

  if (modeKey === "staging") {
    const source = config.scene_source || "preset";
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="v3-field-label">{t.scenery}</div>
        <div className="v3-seg">
          <button type="button" className={"v3-seg-btn" + (source === "preset" ? " active" : "")} onClick={() => onChange("scene_source", "preset")}>{t.preset}</button>
          <button type="button" className={"v3-seg-btn" + (source === "custom" ? " active" : "")} onClick={() => onChange("scene_source", "custom")}>{t.custom}</button>
        </div>
        {source === "preset" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            {stagingScenes(product.product_context_key).map((scene) => {
              const isSelected = (config.scene_key || "auto") === scene.k;
              return (
                <button
                  key={scene.k}
                  type="button"
                  style={{
                    padding: "6px 8px", fontSize: 12, border: "1px solid",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    borderRadius: 4, background: isSelected ? "var(--accent-soft)" : "var(--panel)",
                    cursor: "pointer", textAlign: "left",
                  }}
                  onClick={() => onChange("scene_key", scene.k)}
                >
                  {scene.n}
                </button>
              );
            })}
          </div>
        )}
        {source === "custom" && (
          <textarea
            className="v3-textarea"
            rows={3}
            placeholder={t.stagingCustomPlaceholder}
            value={config.scene_custom || ""}
            onChange={(event) => onChange("scene_custom", event.target.value)}
          />
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={config.multi_shot === "true"}
            onChange={(event) => onChange("multi_shot", String(event.target.checked))}
            style={{ width: 14, height: 14 }}
          />
          {t.multiShot}
        </label>
        {config.multi_shot === "true" && (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={config.skip_board === "true"}
                onChange={(event) => onChange("skip_board", String(event.target.checked))}
                style={{ width: 14, height: 14 }}
              />
              {t.skipBoardShots}
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={config.variation_enabled === "true"}
                onChange={(event) => onChange("variation_enabled", String(event.target.checked))}
                style={{ width: 14, height: 14 }}
              />
              {t.variation}
            </label>
          </>
        )}
      </div>
    );
  }

  if (modeKey === "packshot") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.cameraOrientation}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {packshotOrientations().map((orientation) => {
              const isSelected = (config.camera_angle || "3quarter_left") === orientation.k;
              return (
                <button
                  key={orientation.k}
                  type="button"
                  style={{
                    padding: "5px 10px", fontSize: 12, border: "1px solid",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    borderRadius: 4, background: isSelected ? "var(--accent-soft)" : "var(--panel)",
                    cursor: "pointer",
                  }}
                  onClick={() => onChange("camera_angle", orientation.k)}
                >
                  {orientation.n}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.accessories}</div>
          <input
            type="text"
            className="v3-input"
            placeholder={t.accessoriesPlaceholder}
            value={config.accessories || ""}
            onChange={(event) => onChange("accessories", event.target.value)}
          />
        </div>
      </div>
    );
  }

  if (modeKey === "lifestyle") {
    const source = config.scene_source || "preset";
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="v3-field-label">{t.lifestyleScene}</div>
        <div className="v3-seg">
          <button type="button" className={"v3-seg-btn" + (source === "preset" ? " active" : "")} onClick={() => onChange("scene_source", "preset")}>{t.preset}</button>
          <button type="button" className={"v3-seg-btn" + (source === "custom" ? " active" : "")} onClick={() => onChange("scene_source", "custom")}>{t.custom}</button>
        </div>
        {source === "preset" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {lifestyleScenes(product.product_context_key).map((scene) => {
              const isSelected = (config.scene_key || "universal") === scene.k;
              return (
                <button
                  key={scene.k}
                  type="button"
                  style={{
                    padding: "5px 10px", fontSize: 12, border: "1px solid",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    borderRadius: 4, background: isSelected ? "var(--accent-soft)" : "var(--panel)",
                    cursor: "pointer",
                  }}
                  onClick={() => onChange("scene_key", scene.k)}
                >
                  {scene.n}
                </button>
              );
            })}
          </div>
        )}
        {source === "custom" && (
          <textarea
            className="v3-textarea"
            rows={3}
            placeholder={t.lifestyleCustomPlaceholder}
            value={config.scene_custom || ""}
            onChange={(event) => onChange("scene_custom", event.target.value)}
          />
        )}
      </div>
    );
  }

  if (modeKey === "variants") {
    const defaultVariants = (product.variants_json || [])
      .map((variant) => `${variant.name} | ${variant.description || ""}`)
      .join("\n");

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.variantsList}</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>{t.eachVariantImage}</div>
          <textarea
            className="v3-textarea"
            rows={5}
            placeholder={defaultVariants || t.variantsPlaceholder}
            value={config.custom_variants || defaultVariants}
            onChange={(event) => onChange("custom_variants", event.target.value)}
          />
        </div>
        <div>
          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.packshotOrientation}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {packshotOrientations().map((orientation) => {
              const isSelected = (config.orientation || "3quarter_left") === orientation.k;
              return (
                <button
                  key={orientation.k}
                  type="button"
                  style={{
                    padding: "5px 10px", fontSize: 12, border: "1px solid",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    borderRadius: 4, background: isSelected ? "var(--accent-soft)" : "var(--panel)",
                    cursor: "pointer",
                  }}
                  onClick={() => onChange("orientation", orientation.k)}
                >
                  {orientation.n}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.boardTitle}</div>
          <input
            type="text"
            className="v3-input"
            placeholder={product.name}
            value={config.board_title || ""}
            onChange={(event) => onChange("board_title", event.target.value)}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={config.skip_board === "true"}
            onChange={(event) => onChange("skip_board", String(event.target.checked))}
            style={{ width: 14, height: 14 }}
          />
          {t.skipBoardPhotos}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={config.variation_enabled === "true"}
            onChange={(event) => onChange("variation_enabled", String(event.target.checked))}
            style={{ width: 14, height: 14 }}
          />
          {t.variation}
        </label>
        <BoardOptions config={config} onChange={onChange} />
      </div>
    );
  }

  if (modeKey === "arrangements") {
    const source = config.scene_source || "preset";
    const defaultSceneKeys = defaultArrangementSceneKeys(product.product_context_key);
    const selectedScenes = (config.scene_keys || defaultSceneKeys.join(",")).split(",");

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="v3-field-label">{t.arrangementScenery}</div>
        <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.eachSceneImage}</div>
        <div className="v3-seg">
          <button type="button" className={"v3-seg-btn" + (source === "preset" ? " active" : "")} onClick={() => onChange("scene_source", "preset")}>{t.preset}</button>
          <button type="button" className={"v3-seg-btn" + (source === "custom" ? " active" : "")} onClick={() => onChange("scene_source", "custom")}>{t.customPlural}</button>
        </div>
        {source === "preset" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
            {arrangementScenes().map((scene) => {
              const isSelected = selectedScenes.includes(scene.k);
              return (
                <button
                  key={scene.k}
                  type="button"
                  style={{
                    padding: "6px 8px", fontSize: 12, border: "1px solid",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    borderRadius: 4, background: isSelected ? "var(--accent-soft)" : "var(--panel)",
                    cursor: "pointer", textAlign: "left",
                  }}
                  onClick={() => onChange("scene_keys", toggleCsvValue(config.scene_keys || defaultSceneKeys.join(","), scene.k))}
                >
                  {scene.n}
                </button>
              );
            })}
          </div>
        ) : (
          <textarea
            className="v3-textarea"
            rows={5}
            placeholder={t.arrangementsCustomPlaceholder}
            value={config.custom_scenes || ""}
            onChange={(event) => onChange("custom_scenes", event.target.value)}
          />
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={config.skip_board === "true"}
            onChange={(event) => onChange("skip_board", String(event.target.checked))}
            style={{ width: 14, height: 14 }}
          />
          {t.skipBoardPhotos}
        </label>
        <BoardOptions config={config} onChange={onChange} />
      </div>
    );
  }

  if (modeKey === "dimensions") {
    const dimensionEntries = Object.entries(product.dimensions_json || {}).filter(([, value]) => value !== null && value !== undefined && String(value).length > 0);
    const selectedDimensions = (config.dimension_keys || dimensionEntries.map(([key]) => key).join(",")).split(",");

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="v3-field-label">{t.dimensionsToShow}</div>
        {dimensionEntries.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--muted)" }}>{t.noDimensions}</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {dimensionEntries.map(([key, value]) => {
              const isSelected = selectedDimensions.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  style={{
                    padding: "5px 10px", fontSize: 12, border: "1px solid",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    borderRadius: 4, background: isSelected ? "var(--accent-soft)" : "var(--panel)",
                    cursor: "pointer",
                  }}
                  onClick={() => onChange("dimension_keys", toggleCsvValue(config.dimension_keys || dimensionEntries.map(([dimensionKey]) => dimensionKey).join(","), key))}
                >
                  {key}: {value}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (modeKey === "callouts" || modeKey === "feature_board") {
    const defaultFeatures = linesFromFeatures(product.features_json);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div className="v3-field-label" style={{ marginBottom: 6 }}>
            {modeKey === "callouts" ? t.calloutsLabel : t.featureBoardLabel} {t.lineNameFocus}
          </div>
          <textarea
            className="v3-textarea"
            rows={5}
            placeholder={defaultFeatures || t.featuresPlaceholder}
            value={config.features || defaultFeatures}
            onChange={(event) => onChange("features", event.target.value)}
          />
        </div>
        {modeKey === "callouts" && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={config.skip_board === "true"}
              onChange={(event) => onChange("skip_board", String(event.target.checked))}
              style={{ width: 14, height: 14 }}
            />
            {t.skipBoardHeroDetails}
          </label>
        )}
        {modeKey === "callouts" && <BoardOptions config={config} onChange={onChange} />}
      </div>
    );
  }

  if (modeKey === "trust_board") {
    const defaultCerts = (product.certifications_json || []).join("\n");
    return (
      <div>
        <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.certsLabel}</div>
        <textarea
          className="v3-textarea"
          rows={4}
          placeholder={defaultCerts || t.certsPlaceholder}
          value={config.certifications || defaultCerts}
          onChange={(event) => onChange("certifications", event.target.value)}
        />
      </div>
    );
  }

  if (modeKey === "faq_board") {
    return (
      <div>
        <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.faqLabel}</div>
        <textarea
          className="v3-textarea"
          rows={5}
          placeholder={t.faqPlaceholder}
          value={config.faqs || ""}
          onChange={(event) => onChange("faqs", event.target.value)}
        />
      </div>
    );
  }

  if (modeKey === "custom") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="v3-field-label" style={{ marginBottom: 4 }}>{t.customLabel}</div>
        <textarea
          className="v3-textarea"
          rows={4}
          placeholder={t.customPlaceholder}
          value={config.description || ""}
          onChange={(event) => onChange("description", event.target.value)}
        />
        <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.5 }}>
          {t.customHint}
        </div>
      </div>
    );
  }

  if (modeKey === "problem_solution_board") {
    return (
      <div>
        <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.problemSolutionLabel}</div>
        <textarea
          className="v3-textarea"
          rows={5}
          placeholder={t.problemSolutionPlaceholder}
          value={config.problem_solutions || ""}
          onChange={(event) => onChange("problem_solutions", event.target.value)}
        />
      </div>
    );
  }

  return null;
}
